import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { PurchaseOrderItem } from '@domain/purchasing';
import { useProductInventory } from '@features/inventory/hooks/useInventory';
import { APP_CONFIG } from '../../../../config/app.config';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { Icon, IconSize } from '@shared/icons';

interface MarkAvailableDialogProps {
  open: boolean;
  item: PurchaseOrderItem;
  isUpdating?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const MarkAvailableDialog: React.FC<MarkAvailableDialogProps> = ({
  open,
  item,
  isUpdating = false,
  onClose,
  onConfirm,
}) => {
  const colors = APP_CONFIG.ui.theme.colors;
  const productId = item.product?.id;
  const { inventory, isLoading } = useProductInventory(productId, open);
  const incomingQty = item.quantity ?? 0;
  const currentQty = inventory?.quantity ?? 0;
  const title =
    item.product?.title || item.product?.ean || 'Untitled product';

  return (
    <Dialog
      open={open}
      onClose={isUpdating ? undefined : onClose}
      onClick={(e) => e.stopPropagation()}
      maxWidth="xs"
      fullWidth
    >
      <DialogHeader  >
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
          <Typography variant="h6" color={colors.primaryText}>
            Mark as available
          </Typography>

          <IconButton onClick={onClose} disabled={isUpdating}>
            <Icon icon="X01" size={IconSize.MEDIUM} />
          </IconButton>
        </Stack>
      </DialogHeader>
      <DialogContent>
        <Stack gap={2.5}>
          <Stack direction="row" alignItems="center" gap={2}>
            <Box
              component="img"
              src={item.product?.image || ''}
              alt=""
              sx={{
                width: 48,
                height: 48,
                minWidth: 48,
                minHeight: 48,
                borderRadius: '4px',
                border: `1px solid ${colors.border}`,
                objectFit: 'contain',
                bgcolor: colors.background,
                flexShrink: 0,
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.visibility = 'hidden';
              }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                color={colors.primaryText}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </Typography>
              <Typography variant="caption" color={colors.secondaryText}>
                Incoming: {incomingQty} items
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              border: `1px solid ${colors.border}`,
              borderRadius: 1,
              px: 2,
              py: 1.5,
            }}
          >
            <Typography variant="caption" color={colors.secondaryText}>
              Current inventory
            </Typography>
            {isLoading ? (
              <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                <CircularProgress size={14} />
                <Typography variant="body2" color={colors.secondaryText}>
                  Loading…
                </Typography>
              </Stack>
            ) : (
              <Typography
                variant="h6"
                color={colors.primaryText}
                sx={{ mt: 0.25, fontWeight: 600 }}
              >
                {currentQty} items
              </Typography>
            )}
          </Box>

          <Typography variant="body2" color={colors.secondaryText}>
            Confirm that current inventory is already updated and includes these{' '}
            {incomingQty} incoming items before marking this purchase order item
            as available.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions

        sx={{
          p: 3,
          borderTop: 1,
          borderColor: 'divider',
          justifyContent: 'flex-end',
        }}
      >
       
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isUpdating || isLoading}
          startIcon={
            isUpdating ? (
              <CircularProgress color="inherit" size={16} />
            ) : undefined
          }
        >
          Confirm available
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MarkAvailableDialog;
